import type { OrderStatus, PaymentMethod } from '../../types/Order';
import type { OrderEvent } from './types';

export interface TimelineActor {
    type: OrderEvent['actorType'];
    name: string | null;
}

type Change =
    | { kind: 'created' }
    | { kind: 'status'; from: OrderStatus | null; to: OrderStatus }
    | { kind: 'paid'; method: PaymentMethod | null }
    | { kind: 'unpaid' }
    | { kind: 'method'; method: PaymentMethod }
    | { kind: 'note'; from: string | null; to: string | null };

export type TimelineRow = Change & {
    id: string;
    at: string;
    actor: TimelineActor;
    /** The row was written by the migration for an order that predates the history: its time is approximate. */
    estimated: boolean;
    /** Status rows: how long the order stayed in the previous state. */
    spent?: { status: OrderStatus; seconds: number };
};

/** Payment status and method written by one action (or two calls right after each other) read as one row. */
const MERGE_MS = 5_000;
const isPayment = (e: OrderEvent) => e.type === 'payment_status' || e.type === 'payment_method';
const mergesWith = (a: OrderEvent, b: OrderEvent) =>
    isPayment(a) &&
    isPayment(b) &&
    a.type !== b.type &&
    (a.type === 'payment_status' ? a : b).to === 'paid' &&
    Math.abs(Date.parse(b.createdAt) - Date.parse(a.createdAt)) <= MERGE_MS;

/** One row per change, oldest first. Moves backwards, cancels and reopens are rows like any other; nothing is collapsed. */
export const buildTimeline = (events: readonly OrderEvent[]): TimelineRow[] => {
    const rows: TimelineRow[] = [];
    let prev: { at: number; status: OrderStatus } | null = null;
    for (let i = 0; i < events.length; i++) {
        const e = events[i];
        if (!e) continue;
        const base = { id: e.id, at: e.createdAt, actor: { type: e.actorType, name: e.actorName }, estimated: e.actorType === 'system' };
        const at = Date.parse(e.createdAt);
        if (e.type === 'created') {
            rows.push({ ...base, kind: 'created' });
            prev = { at, status: 'pending' };
        } else if (e.type === 'status') {
            const to = e.to as OrderStatus;
            rows.push({
                ...base,
                kind: 'status',
                from: e.from as OrderStatus | null,
                to,
                ...(prev ? { spent: { status: prev.status, seconds: Math.max(0, (at - prev.at) / 1000) } } : {}),
            });
            prev = { at, status: to };
        } else if (e.type === 'note') {
            rows.push({ ...base, kind: 'note', from: e.from, to: e.to });
        } else {
            const next = events[i + 1];
            const pair = next && mergesWith(e, next) ? next : null;
            const method = ((e.type === 'payment_method' ? e : pair?.type === 'payment_method' ? pair : null)?.to ?? null) as PaymentMethod | null;
            const paid = (e.type === 'payment_status' ? e : pair)?.to === 'paid';
            if (pair) i++;
            if (paid) rows.push({ ...base, kind: 'paid', method });
            else if (e.type === 'payment_status') rows.push({ ...base, kind: 'unpaid' });
            else if (method) rows.push({ ...base, kind: 'method', method });
        }
    }
    return rows;
};
